import ConsentDisclosure from "components/ConsentDisclosure";
import NoticeBanner from "components/NoticeBanner";
import Skeleton from "components/Skeleton";
import { useUser } from "context/UserContext";
import { WizardMaskedField, WizardTextField } from "components/WizardFields";

import { accountHasName, type Account } from "../account";
import {
  callLineFor,
  contactDisclosure,
  contactErrorMessage,
  type ContactError,
} from "../copy";
import { leadKindOf, needsSubmitterName } from "../flow";
import {
  emailError,
  nameError,
  PHONE_REJECTED_MESSAGE,
  phoneError,
} from "../validation";
import SignedInAs from "./SignedInAs";
import StepFrame, { gated, useContinueGate } from "./StepFrame";
import type { StepProps } from "./types";

export interface ContactSubmitState {
  submitting: boolean;
  error: ContactError | null;
  /** The server refused the phone number as not a real one. */
  phoneRejected: boolean;
}

export default function ContactStep(
  props: StepProps & {
    submit: ContactSubmitState;
    onPhoneEdited: () => void;
  },
) {
  const { answers, update, onBack, submit, onPhoneEdited, account, onSignOut } =
    props;
  const { loading: authPending } = useUser();
  const { attempted, tryContinue } = useContinueGate(props, "contact");
  const askName =
    needsSubmitterName(answers) && !(account && accountHasName(account));
  const askEmail = account === null || account.email === "";
  const askPhone =
    account === null || account.phone === "" || submit.phoneRejected;
  const line = callLineFor(leadKindOf(answers));
  const setSubmitter = (
    key: "firstName" | "lastName" | "email" | "phone",
    value: string,
  ) =>
    update((current) => ({
      ...current,
      submitter: { ...current.submitter, [key]: value },
    }));
  const phoneMessage = submit.phoneRejected
    ? PHONE_REJECTED_MESSAGE
    : phoneError(answers.submitter.phone);
  return (
    <StepFrame
      answers={answers}
      step="contact"
      onBack={onBack}
      onContinue={tryContinue}
      busy={submit.submitting}
      continueDisabled={authPending}
    >
      {authPending ? (
        <ContactSkeleton />
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {account && <AccountValues account={account} />}
            {askName && (
              <div className="grid grid-cols-1 gap-4 2xs:grid-cols-2">
                <WizardTextField
                  id="submitter-first-name"
                  label="First name"
                  value={answers.submitter.firstName}
                  onChange={(value) => setSubmitter("firstName", value)}
                  error={gated(
                    attempted,
                    nameError(answers.submitter.firstName, "First name"),
                  )}
                  autoComplete="given-name"
                  autoFocus
                />
                <WizardTextField
                  id="submitter-last-name"
                  label="Last name"
                  value={answers.submitter.lastName}
                  onChange={(value) => setSubmitter("lastName", value)}
                  error={gated(
                    attempted,
                    nameError(answers.submitter.lastName, "Last name"),
                  )}
                  autoComplete="family-name"
                />
              </div>
            )}
            {askEmail && (
              <WizardTextField
                id="submitter-email"
                label="Email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={answers.submitter.email}
                onChange={(value) => setSubmitter("email", value)}
                error={gated(attempted, emailError(answers.submitter.email))}
                autoFocus={!askName}
              />
            )}
            {askPhone && (
              <WizardMaskedField
                kind="phone"
                id="submitter-phone"
                label="Mobile phone"
                value={answers.submitter.phone}
                onChange={(value) => {
                  onPhoneEdited();
                  setSubmitter("phone", value);
                }}
                error={
                  submit.phoneRejected
                    ? phoneMessage
                    : gated(attempted, phoneMessage)
                }
              />
            )}
          </div>
          {account && (
            <SignedInAs
              account={account}
              onSignOut={onSignOut}
              className="mt-5"
            />
          )}
          {submit.error && (
            <div className="mt-5">
              <NoticeBanner tone="error">
                {contactErrorMessage(submit.error)}{" "}
                <a
                  href={line.href}
                  className="ph-no-capture font-bold underline"
                >
                  {line.label}
                </a>
              </NoticeBanner>
            </div>
          )}
          <p className="mt-5 text-sm leading-normal text-oat-700">
            <ConsentDisclosure
              disclosure={contactDisclosure(account !== null)}
            />
          </p>
        </>
      )}
    </StepFrame>
  );
}

/** The fields and the disclosure depend on who the visitor is, so until
 * that is known they are held as bars of the same footprint. */
function ContactSkeleton() {
  return (
    <div role="status">
      <span className="sr-only">Loading</span>
      <div className="flex flex-col gap-4">
        <Skeleton className="h-[72px] w-full rounded-xl" />
        <Skeleton className="h-[72px] w-full rounded-xl" />
      </div>
      <div className="mt-5 flex flex-col gap-2">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-4/5" />
      </div>
    </div>
  );
}

/** The signed-in visitor's own details, shown as values: the form
 * submits them as they are. */
function AccountValues({ account }: { account: Account }) {
  const rows = [
    {
      label: "Name",
      value: accountHasName(account)
        ? `${account.firstName} ${account.lastName}`
        : "",
    },
    { label: "Email", value: account.email },
    { label: "Mobile phone", value: account.phone },
  ].filter((row) => row.value !== "");
  if (rows.length === 0) return null;
  return (
    <dl className="flex flex-col gap-3 rounded-xl border border-oat-200 bg-white px-4 py-3">
      {rows.map((row) => (
        <div key={row.label}>
          <dt className="text-sm font-bold text-oat-700">{row.label}</dt>
          <dd className="ph-no-capture break-words text-base text-soil">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
